import { Check, Zap } from "lucide-react";
import { Reveal, SectionTag, buyBundle, PRICE, COMPARE_PRICE } from "./shared";

const TIERS = [
  {
    units: 1,
    name: "Solo",
    price: 39.99,
    tag: null,
    blurb: "One X8 for your desk, your car, or your bag.",
    features: ["1 × X8 Violent Fan", "USB-C cable + manual", "Tracked shipping $4.99", "30-day guarantee"],
  },
  {
    units: 2,
    name: "Duo",
    price: 69.99,
    tag: "Most popular",
    blurb: "One for the desk, one for the glovebox. Or one for a friend.",
    features: ["2 × X8 Violent Fan", "$35.00 per unit", "Tracked shipping $4.99", "30-day guarantee"],
  },
  {
    units: 3,
    name: "Squad",
    price: 89.99,
    tag: "Best value",
    blurb: "Max out the discount. Gifts sorted for the year.",
    features: ["3 × X8 Violent Fan", "$30.00 per unit", "Tracked shipping $4.99", "30-day guarantee"],
  },
];

export const Bundles = () => (
  <section id="pricing" data-testid="pricing-section" className="border-t border-steel bg-carbon/40">
    <div className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-36">
      <Reveal>
        <SectionTag index="07" label="Arsenal Builder" />
        <div className="mt-6 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <h2 className="max-w-3xl font-display text-3xl font-black uppercase leading-[0.95] tracking-tighter md:text-6xl">
            One is never<br /><span className="text-volt">enough.</span>
          </h2>
          <p className="max-w-sm text-sm leading-relaxed text-smoke md:text-base">
            Stack units, slash the price. Every bundle ships free with the same 30-day
            money-back guarantee.
          </p>
        </div>
      </Reveal>

      <div className="mt-14 grid gap-4 md:mt-20 lg:grid-cols-3">
        {TIERS.map((t, i) => {
          const hot = t.units === 2;
          const perUnit = (Math.round(Math.round(t.price * 100) / t.units) / 100).toFixed(2);
          const strike = t.units === 1 ? COMPARE_PRICE : PRICE * t.units;
          const save = (strike - t.price).toFixed(2);
          return (
            <Reveal key={t.units} delay={0.08 * i}>
              <div
                data-testid={`bundle-card-${t.units}`}
                className={`relative flex h-full flex-col border p-8 transition-transform duration-300 md:p-10 ${
                  hot
                    ? "border-volt bg-volt text-ink lg:-translate-y-4"
                    : "border-steel bg-ink hover:-translate-y-2"
                }`}
              >
                {t.tag && (
                  <span
                    data-testid={`bundle-tag-${t.units}`}
                    className={`absolute -top-3 left-8 px-3 py-1 font-mono text-[10px] font-bold tracking-[0.25em] uppercase ${
                      hot ? "bg-ink text-volt" : "bg-volt text-ink"
                    }`}
                  >
                    {t.tag}
                  </span>
                )}

                <div className="flex items-baseline justify-between">
                  <span className={`font-display text-6xl font-black tracking-tighter md:text-7xl ${hot ? "" : "text-outline"}`}>
                    ×{t.units}
                  </span>
                  <span className={`font-mono text-[10px] tracking-[0.3em] uppercase ${hot ? "text-ink/70" : "text-smoke"}`}>
                    {t.name}
                  </span>
                </div>

                <div className="mt-8">
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-4xl font-black tracking-tight" data-testid={`bundle-price-${t.units}`}>
                      ${t.price}
                    </span>
                    <span className={`font-mono text-sm line-through ${hot ? "text-ink/50" : "text-smoke"}`}>
                      ${strike.toFixed(2)}
                    </span>
                  </div>
                  <div className={`mt-1 font-mono text-[10px] tracking-[0.25em] uppercase ${hot ? "text-ink/70" : "text-volt"}`}>
                    ${perUnit} per fan — save ${save}
                  </div>
                </div>

                <p className={`mt-6 text-sm leading-relaxed ${hot ? "text-ink/80" : "text-smoke"}`}>{t.blurb}</p>

                <ul className="mt-6 flex flex-col gap-2.5">
                  {t.features.map((f) => (
                    <li key={f} className={`flex items-center gap-2.5 font-mono text-xs ${hot ? "text-ink" : "text-foreground/90"}`}>
                      <Check className={`h-4 w-4 shrink-0 ${hot ? "text-ink" : "text-volt"}`} strokeWidth={3} />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`bundle-buy-${t.units}`}
                  onClick={() => buyBundle(t.units)}
                  className={`mt-10 flex items-center justify-center gap-2 border px-6 py-4 font-mono text-xs font-bold tracking-[0.2em] uppercase transition-colors duration-200 ${
                    hot
                      ? "border-ink bg-ink text-volt hover:bg-transparent hover:text-ink"
                      : "border-volt bg-volt text-ink hover:bg-transparent hover:text-volt"
                  }`}
                >
                  <Zap className="h-4 w-4" />
                  Get {t.units === 1 ? "the X8" : `${t.units} fans`}
                </button>
              </div>
            </Reveal>
          );
        })}
      </div>

      <Reveal delay={0.2}>
        <p className="mt-10 font-mono text-[10px] tracking-[0.25em] uppercase text-smoke" data-testid="bundle-note">
          Checkout right here — card or PayPal. Volume pricing applied automatically, flat $4.99 tracked shipping on every bundle.
        </p>
      </Reveal>
    </div>
  </section>
);
